import type { WorkflowRecommendation } from "@/lib/workflowAdvisor";

// Recommended next step + "Or explore" alternatives, each with its rationale.
// Shared by the Where-am-I map overlay (WorkflowPanel) and the Analyze page's
// next-step card so the two surfaces give identical, advisor-driven guidance.

interface Props {
  recs: WorkflowRecommendation[];
  onAct: (rec: WorkflowRecommendation) => void;
  /** Open the guide/walkthrough for a recommendation, optionally scrolling to a
   *  section. Optional so the list still renders (without guide links) when no
   *  handler is wired. */
  onOpenGuide?: (walkthroughId: string, section?: string) => void;
}

function GuideLink({
  id,
  section,
  onOpenGuide,
}: {
  id: string;
  section?: string;
  onOpenGuide: (id: string, section?: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenGuide(id, section)}
      className="shrink-0 text-[11px] text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
    >
      Guide ↗
    </button>
  );
}

export function RecommendationList({ recs, onAct, onOpenGuide }: Props) {
  const primary = recs[0] ?? null;
  const alternatives = recs.slice(1, 4);

  return (
    <div className="flex flex-col gap-3">
      {primary && (
        <div className="rounded-lg border border-primary/30 bg-gradient-to-b from-primary/15 to-primary/5 p-3">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-primary">
            {primary.status === "blocked" ? "Gated" : "Recommended"}
          </div>
          <div className="mb-1 text-sm font-bold leading-tight">{primary.title}</div>
          <p className="mb-2 text-xs text-muted-foreground">{primary.rationale}</p>
          {primary.unlocks && (
            <p className="mb-2 text-[11px] text-muted-foreground/80">Unlocks: {primary.unlocks}</p>
          )}
          {primary.checklist.length > 0 && (
            <ul className="mb-2 space-y-0.5 text-[11px] text-muted-foreground">
              {primary.checklist.map((c, i) => (
                <li key={i}>{c.done ? "☑" : "☐"} {c.label}</li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-3">
            {primary.status === "blocked" ? (
              <div className="inline-flex h-8 items-center rounded-md border border-border bg-muted px-3 text-xs font-semibold text-muted-foreground">
                {primary.title}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onAct(primary)}
                data-testid="workflow-primary-cta"
                className="inline-flex h-8 items-center rounded-md bg-primary px-3 text-xs font-semibold text-[#11111b]"
              >
                {primary.title} →
              </button>
            )}
            {onOpenGuide && primary.walkthroughId && (
              <GuideLink id={primary.walkthroughId} section={primary.walkthroughSection} onOpenGuide={onOpenGuide} />
            )}
          </div>
        </div>
      )}

      {alternatives.length > 0 && (
        <div>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Or explore
          </div>
          <ul className="space-y-1">
            {alternatives.map((r) => (
              <li key={r.id} className="flex items-stretch gap-1.5">
                <button
                  type="button"
                  onClick={() => onAct(r)}
                  className="flex-1 rounded-md border border-border px-2.5 py-1.5 text-left hover:border-primary/50"
                >
                  <div className="text-xs font-semibold">{r.title}</div>
                  <div className="text-[11px] text-muted-foreground">{r.rationale}</div>
                </button>
                {onOpenGuide && r.walkthroughId && (
                  <div className="flex items-center">
                    <GuideLink id={r.walkthroughId} section={r.walkthroughSection} onOpenGuide={onOpenGuide} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
